// PromptEditor.tsx · v12.10.27 (#2168 a cleared box stays empty - no default refill, no caret jump; Reset restores it) · 2026-10-31 · VoiceSpectrum AI
// PromptEditor.tsx · v12.10.18 (#1863 addendum's pretty name) · 2026-10-22 · VoiceSpectrum AI
// PromptEditor.tsx - v12.10.17 (#1747 the ninth entity 'contact' - labelled, typed-prefix aware, and left out of the general pickers; the ID check card offers it).
// PromptEditor.tsx · v12.10.16 (#1674 previewWhenBlank - the Text card Re-ask previews the offer) · 2026-10-14 · VoiceSpectrum AI
// PromptEditor.tsx · v12.10.13 (#1600 the preview resolves {call.timeGreeting} in the tab's language) · 2026-10-10 · VoiceSpectrum AI
// PromptEditor.tsx - v12.10.12 (#1519 the eighth entity "call" - {call.timeGreeting}, label "Time greeting").
// PromptEditor.tsx · v12.10.11.1 (#1444 readable labels for run-together field keys; #1481 lang per language tab, unknown {token} flagged on every box) · 2026-10-09
// PromptEditor.tsx · v12.10.11 (#1458 new text uses {organization.name} - the vocabulary token; {organization.practiceName} / {tenant.practiceName} stay readable as aliases) · 2026-10-08
// PromptEditor.tsx · v12.10.10 (#1408: every box declares platformDefault OR silentWhenBlank - enforced by the type; the default is ALWAYS written text with the Template default badge, never grey; no placeholder sentences; 'Blank = nothing is said' under silent boxes; Auto-translate + Reset always on non-primary tabs) · 2026-10-08 · VoiceSpectrum AI
// PromptEditor.tsx - v12.10.8 (#1227 a Vocabulary field or List switched Off is not offered by the token pickers - catalogGroups, one place; #1190 identifiers gone from AiAgentTokenSettings).
// PromptEditor.tsx - v12.9.26 (#950 the token warning knows every token the preview resolves; #946): no Machine-translated badge on any prompt box.
// PromptEditor.tsx — v12.9.24 (#904 isCodeShaped widened - locale anagrams and all-short-word names; #911 MachineFilledContext: publish-time machine translations carry the Machine-translated badge)
// PromptEditor.tsx — v12.9.24 (#925 organization.agentName label: 'Agent name')
// PromptEditor.tsx — v12.4.16 (#498 external.* is the seventh entity: default label, field labels, alias completion;
//                      #499 a custom field carries a SOURCE and may hang off a platform entity)
// PromptEditor.tsx — v12.3.9 (#238-A)
// SHARED prompt editor: the token-picker + live PREVIEW + ▶ Play control every spoken-text card uses.
// Lifted VERBATIM out of WorkflowModalSnapshotPage.tsx (where it had lived since v12.2.20.0) so the
// Knowledge Base "Quick Answer" editor renders the SAME control instead of a bare TokenPicker — smoke
// finding A on 12.3.8. Behaviour is unchanged; only the definition moved, and the workflow page now
// imports it from here.
// NOTE ON STYLES: the .tenant-prompt-* / .tenant-play-* / .tenant-xl-* rules live in
// pages/workflow-modal-snapshot.css and .ai-agent-panel in pages/ai-agent.css. T2 has NO route-level
// code splitting (App.tsx imports every page statically), so Vite emits ONE stylesheet and both are
// present on every route, Knowledge Base included.
import { createContext, useContext, useEffect, useMemo, useState } from 'react';   // v12.10.27 (#2168): useEffect
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import { TokenPicker, type TokenGroup } from './aiagent/TokenPicker';
import { resolveTokens, resolveTokenSegments, RESOLVER_KNOWN_TOKENS, type TokenContext } from '../lib/tokenPreview';

// v12.2.20.0 (PRE-TOK2): the slice of GET /t2/settings/ai-agent used to resolve preview tokens the
// same way the runtime does. practiceName / supportedLanguages / primaryLanguage are computed
// server-side (AiAgentSettingsController) from the SAME tenant data the call reads.
export type AiAgentTokenSettings = {
  languages?: Array<{ code: string; voice?: string }>;
  practiceName?: string;
  supportedLanguages?: string;
  primaryLanguage?: string;
  orgPrimaryPhone?: string;
  // v12.10.8 (#1190): Identity extraction is gone - the API no longer sends identifiers; kept optional for old responses.
  identifiers?: Array<{ name: string; key: string; type?: string; entity?: string; field?: string }>;
};

// v12.3.10 (#297): the organization Identity block from GET /t2/settings/organization. The PREVIEW now
// resolves the SAME set of {organization.*} tokens the engine does - previously the picker offered
// tokens (timezone, and everything below) that no resolver on either side could fill.
export type OrgIdentity = {
  legalName?: string | null; displayName?: string | null; websiteUrl?: string | null;
  addressText?: string | null; primaryEmail?: string | null; primaryPhone?: string | null;
  primaryContactName?: string | null; primaryContactRole?: string | null;
  industry?: string | null; industryOther?: string | null; timezoneId?: string | null;
};

export function tokenContextFrom(s: AiAgentTokenSettings | undefined, org?: OrgIdentity): TokenContext {
  return {
    practiceName: s?.practiceName ?? '',
    phone: s?.orgPrimaryPhone ?? '',
    supportedLanguages: s?.supportedLanguages ?? '',
    primaryLanguage: s?.primaryLanguage ?? '',
    timezone: org?.timezoneId ?? '',
    legalName: org?.legalName ?? '',
    email: org?.primaryEmail ?? '',
    website: org?.websiteUrl ?? '',
    address: org?.addressText ?? '',
    // Mirrors EffectiveConfigResolver: a tenant who picked "Other" and typed their own wins.
    industry: (org?.industryOther && org.industryOther.trim()) ? org.industryOther : (org?.industry ?? ''),
    contactName: org?.primaryContactName ?? '',
    contactRole: org?.primaryContactRole ?? '',
  };
}

export const ENTITY_DEFAULT_LABEL: Record<string, string> = { caller: 'Caller', subject: 'Patient', request: 'Appointment', provider: 'Doctor', location: 'Office', organization: 'Organization', external: 'External system', call: 'Call', contact: 'Contact' };   // v12.10.12 (#1519): call is the eighth; v12.10.17 (#1747): contact the ninth   // v12.2.43.2 (#235/#236) + v12.3.10 (#298): organization is the sixth entity + v12.4.16 (#498): external is the seventh
export const FIELD_PRETTY: Record<string, string> = { firstName: 'First name', lastName: 'Last name', name: 'Full name', dob: 'Date of birth', zip: 'ZIP', address1: 'Address 1', address2: 'Address 2', city: 'City', state: 'State', county: 'County', country: 'Country', phone: 'Phone', memberId: 'Member ID', date: 'Date', time: 'Time', newDate: 'New date', newTime: 'New time', place: 'Place', provider: 'Provider', reason: 'Reason', visitType: 'Visit type', description: 'Description', role: 'Role', location: 'Location', legalName: 'Business name (legal)', email: 'Email', website: 'Website', address: 'Address', timezone: 'Timezone', industry: 'Industry', contactName: 'Primary contact name', contactRole: 'Primary contact role', supported_languages: 'Supported languages', primary_language: 'Primary language', agentName: 'Agent name', timeGreeting: 'Time greeting', addendum: 'Addendum',
  // v12.4.0 (#389): the platform-managed field #371 added in 12.3.14 and never registered here.
  referenceNumber: 'Reference number',
  // v12.4.16 (#498): the external.* family. `id` in particular would otherwise pretty-print as "Id" through
  // prettyField anyway, but naming them here keeps the four platform fields of an entity declared in one place.
  id: 'Id', reference: 'Reference', message: 'Message', status: 'Status' };

// v12.3.10 (#296): the vocabulary CATALOG as served by GET /t2/settings/organization. Entities and their
// fields are platform-managed (BDR-001 v1.1); only the label is the tenant's. This replaced three
// hardcoded, mutually-disagreeing lists - the picker could offer a token the engine could not resolve.
// v12.4.16 (#518/#499): a served field now carries its declared Type and Source. Optional here because the
// screens that only need a label must keep working against an older response while a deploy is in flight.
export interface VocabCatalogField { key: string; label: string; type?: string; source?: string }
export interface VocabCatalogEntity { token: string; defaultLabel: string; what: string; sub: string; fields: VocabCatalogField[] }
// v12.3.11 (#311): tenant-registered custom capture fields, keyed "entity.field".
// v12.4.16 (#518/#499): the type union was FOUR words while the catalog declares nine, so a tenant field
// typed `zip` or `choice` did not fit the type that describes it. It is the catalog's set now (FieldTypes in
// VocabularyCatalog.cs) and is kept as `string` rather than restated as a union, because a union here would be
// a fourth copy of a list that already exists in three places - which is the drift #296 removed.
// `source` is how it gets filled and `listKey` is the List a `choice` field is backed by (#500).
export interface CustomField { label: string; type: string; source?: string; listKey?: string; listColumn?: string; tenantOwned?: boolean; isOn?: boolean }   // v12.10.10 (#1406): listColumn   // v12.10.8 (#1227): isOn false = hidden from pickers
// v12.7.3 (#649): `lists` rides the SAME response and the SAME react-query key the vocabulary already uses,
// so making every picker list-aware costs no extra request.
export interface RefListLite { key: string; name: string; isOn?: boolean; offerCap?: number | null; entries?: { active?: boolean }[] }   // v12.10.8 (#1227); v12.10.10 (#1406): cap + entries for the C2 check
export interface OrgVocabResponse { vocabulary?: Record<string, string>; vocabularyCatalog?: VocabCatalogEntity[]; identity?: OrgIdentity; customFields?: Record<string, CustomField>; lists?: Record<string, RefListLite>; fieldOverrides?: Record<string, { label?: string; cleanup?: string; isOn?: boolean; listKey?: string; listColumn?: string }>; }
// v12.8.0 (#659): what the tenant has said about a PLATFORM field - their label and their input cleanup.

// Build the Insert-token panel from the catalog: the GROUP shows the tenant's word ("Visitor"), the
// inserted text is always the canonical token ({subject.firstName}). Renaming a label therefore changes
// every picker instantly and rewrites nothing that is stored.
// v12.4.16 (#499): A TENANT-ADDED FIELD MUST BE OFFERED WHEREVER A PLATFORM ONE IS.
//
// `custom` is a THIRD, OPTIONAL argument rather than a change to the first two, so every existing call site
// keeps compiling and keeps its current behaviour until it passes one. Without it #499 would have shipped a
// screen that creates subject.accountNumber and a picker that never offers it - a registry that records and
// shows nothing, which is the exact failure #311/#314 were written to end, arriving from a new direction.
//
// A tenant field on a PLATFORM entity joins that entity's group, because the tenant thinks of it as one more
// field on "Who called". A field on an entity the catalog does not know gets its own group, labelled with the
// tenant's word for it.
// ── v12.7.3 (#649): THE PLATFORM-MANAGED TOKENS, IN ONE PLACE, FOR EVERY PICKER. ──────────────────────
//
// Tushar: *"why visit.reference number is not coming? that token itself is missing. why this keeps on
// happening"* - and separately, on trying to insert a list token: *"i typed list. nothing came up so token is
// still not available for the list."*
//
// The answer to "why does this keep happening" is that there were THREE catalogs built independently -
// TokenPicker's static default, the designer's availableTokenGroups, and Integrations' - and a token added to
// one reached the others only if someone remembered. request.referenceNumber lived in the designer alone;
// Lists.* was in none of them, even though the engine has resolved {Lists.X} since #613.
//
// Neither belongs to the vocabulary catalog: no step captures them and no tenant declares them, so they are
// invisible to catalogGroups' entity walk. That is the #297 trap - a token that resolves perfectly and no
// tenant can find. They are appended HERE, once, so every consumer of catalogGroups gets them by construction
// rather than by memory.
export function platformGroups(lists: Record<string, RefListLite> = {}, custom?: Record<string, CustomField>): TokenGroup[] {
  const out: TokenGroup[] = [
    // Belongs to the CALL, not to the caller or the request - the engine seeds it on every call.
    { label: 'This call', tokens: ['request.referenceNumber'] },
  ];
  // v12.10.10 (#1406): a field TIED to a list adds its offer token {lists.provider.name} - in a prompt the agent offers the
  // list's Active entries (top N = the list's cap). Two-part tokens stay everywhere else. Only fields tied by a spoken
  // column (name / description) offer anything; an id is never read out.
  if (custom) {
    const on = new Set(Object.values(lists).filter((l) => l?.isOn !== false).map((l) => (l?.key ?? '').toLowerCase()));
    const offers = Object.entries(custom)
      .filter(([, f]) => !!f?.listKey && on.has((f.listKey ?? '').toLowerCase()) && ['name', 'description', ''].includes((f.listColumn ?? '').toLowerCase()))
      .map(([k]) => `lists.${canonToken(k)}`);
    if (offers.length) out.push({ label: 'Offer from a list', tokens: offers });
    return out;
  }
  const listTokens = Object.values(lists)
    .filter((l) => l?.isOn !== false)   // v12.10.8 (#1227): an Off list is not offered
    .map((l) => (l?.key ?? '').trim())
    .filter(Boolean)
    .map((k) => `Lists.${k}`);
  if (listTokens.length) out.push({ label: 'Lists', tokens: listTokens });
  return out;
}

export function catalogGroups(
  catalog: VocabCatalogEntity[] | undefined,
  vocab: Record<string, string> = {},
  custom: Record<string, CustomField> = {},
  lists: Record<string, RefListLite> = {},
): TokenGroup[] | undefined {
  if (!catalog || catalog.length === 0) return undefined;
  const extra = new Map<string, string[]>();
  // v12.10.8 (#1227): ONE place hides an Off field from every picker built on the catalog (designer, Quick answers,
  // Integrations). Hide only - `known` / labels / types elsewhere still see it, so a token already in use keeps working.
  const off = new Set(Object.entries(custom).filter(([, v]) => v?.isOn === false).map(([k]) => canonToken(k).toLowerCase()));
  for (const rawKey of Object.keys(custom)) {
    const key = canonToken(rawKey);
    if (off.has(key.toLowerCase())) continue;
    const dot = key.indexOf('.');
    if (dot <= 0 || dot === key.length - 1) continue;
    const ent = key.slice(0, dot).toLowerCase();
    const list = extra.get(ent) ?? [];
    if (!list.includes(key)) list.push(key);
    extra.set(ent, list);
  }
  const known = new Set(catalog.map((e) => e.token.toLowerCase()));
  // v12.10.17 (#1747): {contact.*} resolves ONLY on the ID check card's question (the contact being asked about), so it is
  // not offered by every picker - the ID check editor adds it itself (CONTACT_TOKEN_GROUP).
  const groups: TokenGroup[] = catalog.filter((e) => e.token.toLowerCase() !== 'contact').map((e) => ({
    label: vocab[e.token] || e.defaultLabel,
    // `.label` speaks the tenant's own word inside a prompt, so a rename reaches spoken lines too.
    tokens: Array.from(new Set([   // v12.10.10: de-duplicated - a platform field with an override is also in `custom`
      ...e.fields.map((f) => `${e.token}.${f.key}`).filter((t) => !off.has(t.toLowerCase())),
      ...(extra.get(e.token.toLowerCase()) ?? []).filter((t) => !e.fields.some((f) => `${e.token}.${f.key}`.toLowerCase() === t.toLowerCase())),
      `${e.token}.label`,
    ])),
  }));
  for (const [ent, tokens] of extra)
    if (!known.has(ent)) groups.push({ label: vocab[ent] || prettyField(ent), tokens: [...tokens, `${ent}.label`] });
  groups.push(...platformGroups(lists, custom));   // v12.7.3 (#649); v12.10.10 (#1406): offer tokens of tied fields
  return groups;
}

// v12.3.10.1 (#303): ONE hook every consumer uses to get the served catalog + the tenant's labels.
// Before this the workflow drawer built its own token list from BASE_TOKENS/APPOINTMENT_TOKENS, which is why
// 12.3.10 shipped a Quick-Answer picker showing six catalog groups while the workflow drawer still showed
// three `tenant.*` tokens. react-query dedupes by key, so calling this from several components is free.
export function useVocabulary(): { catalog: VocabCatalogEntity[] | undefined; vocab: Record<string, string>; customFields: Record<string, CustomField>; lists: Record<string, RefListLite> } {
  const { data } = useQuery<OrgVocabResponse>({
    queryKey: ['t2-org-vocabulary'],
    queryFn: () => api.get<OrgVocabResponse>('/t2/settings/organization'),
    staleTime: 60_000,
  });
  // ── v12.8.0 (#659): A RENAMED PLATFORM FIELD MUST READ AS ITS NEW NAME EVERYWHERE. ──────────────────
  //
  // `tokenLabel` looks in `custom[token].label` BEFORE it falls back to the catalog, so folding the tenant's
  // overrides into that map here makes every label in the product follow the rename - the designer's token
  // picker, the Stores chips, the ladder's chip row, the integration mappings - with no call-site changes.
  //
  // ONE PLACE, deliberately. #405's note on this very file: a fix applied to a SITE rather than to the
  // pattern left six of twelve editors wired to nothing, and Tushar found the next one himself. An editable
  // name that only showed inside the drawer that edits it would be the same defect as the Input cleanup this
  // release is fixing - a control that takes a value and does nothing with it.
  //
  // No collision is possible: the API refuses a platform key in `customFields` (`ShadowsPlatformField`), so
  // these keys can only come from here.
  //
  // ══ v12.8.1 (#673): AN OVERRIDE CARRIES A LABEL. IT MUST NEVER ANSWER "WHAT TYPE IS THIS FIELD?" ══
  //
  // MINE, introduced with this merge one release ago. The default below used to be
  // `{ label: '', type: 'text' }`, and a PLATFORM field has no `customFields` entry - so renaming one created
  // an entry ASSERTING it was type `text`, which the override had never said. `stepFieldType` consults this
  // map FIRST, by design, so a renamed `subject.firstName` resolved to text; `LadderRungs.AvailableFor('text')`
  // returns no rungs; and the whole If-not-captured block on the step card collapsed to
  // "nothing to try differently on a text field - it cannot be spelled, split or keyed in".
  //
  // On a NAME step. In the release built for that ladder. Tushar had turned Ask-to-Spell on the day before,
  // watched it work on a live call, renamed the field, and it silently vanished.
  //
  // `type` is deliberately absent here rather than defaulted to something better: the entry exists ONLY to
  // carry a label, and anything downstream that needs the type must fall through to the catalog, which is
  // where a platform field's type actually lives (BDR-001 §7.8 / BDR-013 Decision 6 - type is
  // platform-managed; the tenant owns the label). A default is a guess, and this whole defect is one guess
  // reaching every consumer at once - the leverage of "one place, deliberately" working in reverse.
  const merged: Record<string, CustomField> = { ...(data?.customFields ?? {}) };
  for (const [k, ov] of Object.entries(data?.fieldOverrides ?? {}))
    if (ov?.label) merged[k] = { ...(merged[k] ?? { label: '' } as CustomField), label: ov.label };
  // v12.10.8 (#1227): a platform field switched Off carries isOn: false here too (no type is asserted - see #673 above).
  for (const [k, ov] of Object.entries(data?.fieldOverrides ?? {}))
    if (ov?.isOn === false) merged[k] = { ...(merged[k] ?? { label: '' } as CustomField), isOn: false };
  // v12.10.10 (#1406): a PLATFORM field tied to a list carries the tie here too (no type asserted - see #673 above).
  for (const [k, ov] of Object.entries(data?.fieldOverrides ?? {}))
    if (ov?.listKey) merged[k] = { ...(merged[k] ?? { label: '' } as CustomField), listKey: ov.listKey, listColumn: ov.listColumn };
  return { catalog: data?.vocabularyCatalog, vocab: data?.vocabulary ?? {}, customFields: merged, lists: data?.lists ?? {} };
}

// v12.8.2 (#678): the words for a failed preview live HERE, not in the API response. The server returns a
// machine code and a reference; it must never hand us a provider's sentence to render, because on 2026-09-24
// it handed a tenant "402 ... upgrade your subscription at https://play.cartesia.ai/subscription" verbatim.
// The reference is what makes the generic wording actionable: it resolves to the real provider error in the
// server log with one grep.
export function previewFailureText(res: { reason?: string | null; reference?: string | null }): string {
  const ref = res?.reference ? ` (ref ${res.reference})` : '';
  switch (res?.reason) {
    case 'voice_unavailable':
      return `The voice service is unavailable right now \u2014 please contact support${ref}.`;
    case 'voice_not_in_registry':
      return 'That voice is no longer available. Choose another voice for this language.';
    default:
      return `Preview unavailable${ref}.`;
  }
}

// v12.3.11 (#311): pretty-print an unregistered segment, so an unknown field still reads as words.
// v12.10.11.1 (#1444): a run-together lowercase key ("daysofsupply") reads "Days of supply" - split only when every
// part is a known word (same list as the server's FieldLabels), otherwise left as typed.
const FIELD_WORDS = new Set('days day of supply pharmacy name lab labs panel panels strength form medication medications test tests date birth phone number insurance member id provider visit type reason refill refills dose dosage quantity start end time address city state zip code email first last middle policy group plan carrier prescriber doctor office location status note notes result results order ordered appointment preferred contact method language emergency relationship subscriber payer prior authorization auth referral referring specialty symptom symptoms allergy allergies condition primary secondary mail home mobile cell work street account balance amount due payment request requested callback best how many per count size unit units route frequency instructions rx ndc npi dob sex gender age weight height'.split(' '));
function splitRun(s: string): string[] | null {
  if (FIELD_WORDS.has(s)) return [s];
  const best: (string[] | null)[] = new Array(s.length + 1).fill(null);
  best[0] = [];
  for (let i = 1; i <= s.length; i++) {
    for (let j = Math.max(0, i - 14); j < i; j++) {
      const prev = best[j]; const w = s.slice(j, i);
      if (!prev || !FIELD_WORDS.has(w)) continue;
      if (!best[i] || prev.length + 1 < best[i]!.length) best[i] = [...prev, w];
    }
  }
  return best[s.length];
}
export function prettyField(seg: string): string {
  const spaced = seg.replace(/[_-]+/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').trim();
  if (!spaced) return seg;
  const lower = spaced.toLowerCase().split(/\s+/).flatMap((w) => splitRun(w) ?? [w]).join(' ');
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export function tokenLabel(token: string, vocab: Record<string, string> = {}, custom: Record<string, CustomField> = {}): string | undefined {
  // A REGISTERED custom field carries its own label, so {medication.daysOfSupply} reads "Medication › Days of supply".
  const reg = custom[token] ?? custom[Object.keys(custom).find((k) => k.toLowerCase() === token.toLowerCase()) ?? ''];
  if (reg?.label) {
    const dot0 = token.indexOf('.');
    const ent0 = dot0 > 0 ? prettyField(token.slice(0, dot0)) : '';
    return ent0 ? `${ent0} › ${reg.label}` : reg.label;
  }
  const dot = token.indexOf('.');
  if (dot < 0) return undefined;
  let ent = token.slice(0, dot).toLowerCase();
  const fld = token.slice(dot + 1);
  // Stored text may still contain legacy spellings, so LABELLING must understand them. Typing them is a
  // different matter — see makeAliasOf, where v12.3.10.1 (#296) stopped accepting them.
  if (ent === 'patient') ent = 'subject';
  if (ent === 'appointment') ent = 'request';
  if (ent === 'tenant') ent = 'organization';
  const entLabel = vocab[ent] || ENTITY_DEFAULT_LABEL[ent];
  if (!entLabel) return undefined;             // not a vocab entity
  // v12.10.11 (#1458): the organization's name is "Display name" (Settings › Organization), not the person label "Full name".
  if (ent === 'organization' && (fld === 'name' || fld === 'practiceName')) return `${entLabel} › Display name`;
  // v12.3.10 (#296): {x.label} speaks the tenant's own word for the entity.
  if (fld === 'label') return `${entLabel} › the word "${entLabel}"`;
  // v12.4.0 (#389): fall back to prettyField, never the RAW segment. This rendered 'Visit › referenceNumber'
  // in every picker while every neighbour read as a phrase - and prettyField has existed three functions
  // above since #311 for exactly this. The raw fallback meant ANY unregistered field leaked camelCase.
  return `${entLabel} › ${FIELD_PRETTY[fld] || prettyField(fld)}`;
}

// v12.2.43.1 (#236/#246): patient.* is the backward-compat ALIAS for canonical subject.* — canonicalize the
// entity so patient.zip and subject.zip collapse to ONE canonical entry (the engine resolves the alias at runtime).
export const ENTITY_ALIAS: Record<string, string> = { patient: 'subject', appointment: 'request', tenant: 'organization' };
export function canonToken(t: string): string {
  const dot = t.indexOf('.'); if (dot < 0) return t;
  const ent = t.slice(0, dot).toLowerCase();
  return (ENTITY_ALIAS[ent] ?? ent) + t.slice(dot);
}

// v12.2.43.1 (#236/#246): resolve a TYPED entity prefix (patient / subject / a tenant Vocabulary label like
// "visitor") to its canonical entity, so the token autocomplete matches whatever name the author knows while the
// inserted/stored token stays canonical.
export function makeAliasOf(vocab: Record<string, string> = {}) {
  const norm = (x: string) => (x || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  return (typed: string): string | undefined => {
    const t = norm(typed);
    if (!t) return undefined;
    // v12.3.10.1 (#296): legacy spellings are NO LONGER accepted when typed. Tushar: "don't accept legacy
    // and it should not come." The engine still RESOLVES patient.* / appointment.* / tenant.* so every
    // prompt already written keeps working — they are simply not offered, not completed, and not matched.
    // v12.4.16 (#498): + external, so typing `external.` (or the tenant's own word for it) completes.
    for (const ent of ['subject', 'caller', 'request', 'provider', 'location', 'organization', 'external', 'call', 'contact']) {   // v12.10.12 (#1519) + call; v12.10.17 (#1747) + contact
      if (norm(ent) === t) return ent;                          // canonical entity typed directly
      const lbl = vocab[ent] || ENTITY_DEFAULT_LABEL[ent];
      if (lbl && norm(lbl) === t) return ent;                   // tenant vocab label (or default) typed
    }
    return undefined;
  };
}

// v12.2.35.0 (#191): design-time warning when a prompt references a {token} the engine can't resolve
// (not a base/caller token and not captured earlier in this workflow). Unknown tokens are dropped at
// call time and the sentence tidied, so this catches typos like {patient.dob} vs {patient.birthdate}.
export function UnresolvedTokenNote({ text, known }: { text: string; known: string[] }) {
  // v12.2.43.2 (#235): canonicalize BOTH sides (patient.* <-> subject.*) so an alias like {patient.name} is not
  // falsely flagged 'Unrecognized' — the engine resolves the alias at runtime; only this design-time check had
  // drifted after the token list was canonicalized to subject.*.
  // v12.9.26 (#950): the warning and the PREVIEW now agree - every token the preview resolver fills (organization.*,
  // tenant.*) is known here too, whichever list the caller of this note passed. {organization.practiceName} on the
  // voicemail greeting previewed as 'Harlem Medical' while this flagged it as unrecognized.
  const knownSet = useMemo(() => new Set([...known, ...RESOLVER_KNOWN_TOKENS].map(t => canonToken(t).toLowerCase())), [known]);
  const unknown = useMemo(() => {
    const found = new Set<string>();
    const re = /\{([^{}]+)\}/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text ?? '')) !== null) {
      const tok = m[1].trim();
      if (tok && !knownSet.has(canonToken(tok).toLowerCase())) found.add(tok);
    }
    return Array.from(found);
  }, [text, knownSet]);
  if (!unknown.length) return null;
  return (
    <div className="tenant-token-warn" role="note">
      &#9888; Unrecognized token{unknown.length > 1 ? 's' : ''}: {unknown.map(t => '{' + t + '}').join(', ')} &mdash; dropped at call time unless captured earlier.
    </div>
  );
}

// Robust language resolver: keyed by 2-letter code AND full-word code (tenant data sometimes stores 'french'/'german').
// v12.9.21 (#864): a FALLBACK, not a source of truth. `langLabel` prefers the name the API sends; this
// table only covers a row that arrives without a usable one, and the two-letter `short` normalisation that
// `canonLang` needs for full-word codes ('english' -> 'en'). Adding a language does NOT require an entry
// here - if it did, this table would still be failing the test it was logged for.
export const LANGUAGE_INFO: Record<string, { name: string; short: string }> = {
  en: { name: 'English', short: 'EN' }, english: { name: 'English', short: 'EN' },
  es: { name: 'Spanish', short: 'ES' }, spanish: { name: 'Spanish', short: 'ES' },
  fr: { name: 'French', short: 'FR' }, french: { name: 'French', short: 'FR' },
  de: { name: 'German', short: 'DE' }, german: { name: 'German', short: 'DE' },
  hi: { name: 'Hindi', short: 'HI' }, hindi: { name: 'Hindi', short: 'HI' },
  gu: { name: 'Gujarati', short: 'GU' }, gujarati: { name: 'Gujarati', short: 'GU' },
  zh: { name: 'Chinese', short: 'ZH' }, chinese: { name: 'Chinese', short: 'ZH' },
  ar: { name: 'Arabic', short: 'AR' }, arabic: { name: 'Arabic', short: 'AR' },
};

// Settings-protocol language label: friendly name + middot + 2-letter code, e.g. "English · EN" (matches AI Agent pages).
//
// v12.9.21 (#864): THE PRECEDENCE WAS INVERTED, AND THAT IS THE WHOLE DEFECT.
// This consulted LANGUAGE_INFO first and only fell back to the name the API sent. So T2's own eight-language
// table outranked the tenant's configured language list, and a NINTH language - one the platform had fully
// provisioned - rendered as a bare code until somebody edited this file. That fails the architecture
// acceptance test (ARCHITECTURE §7: enabling a language must be data, not code), which is the only reason
// this item existed; nothing was visibly broken.
//
// `displayName` comes from the API and is authoritative. LANGUAGE_INFO is now what it should always have
// been: a FALLBACK for rows that carry no usable name, not an override of the ones that do.
// v12.9.22 (#885): is this a NAME, or the code wearing a hat?
//
// #864's rule - the name the API sends outranks this file's table - is right and stays. But it only makes
// sense for a REAL name, and the old test ("not literally equal to the code") let anything code-shaped
// through: "EN-US" and "US EN" both passed and rendered beside their own abbreviation.
//
// Compared on letters alone, in order AND sorted, so every rearrangement of a code's own letters is caught
// while a real name is not: "English" against "en" matches neither way, and no language's actual name is an
// anagram of its two-letter code.
// v12.9.24 (#904): WIDENED. "US EN" and "ES ES" passed the 12.9.22 test - neither is an anagram of "en"/"es" -
// and rendered as "US EN · EN". A name is also code-shaped when it is an anagram of the row's LOCALE ("en-US"),
// or when EVERY word in it is three letters or fewer: no language's name is made only of such words, and every
// fabricated one ("US EN", "ES ES", "EN US") is. The SAME rule runs server-side in the 12.9.24 data pass
// (LanguageNameCleanup.IsCodeShaped) - change one, change both.
export function isCodeShaped(given: string, code: string, locale?: string): boolean {
  const letters = (v: string) => v.toLowerCase().replace(/[^a-z]/g, '');
  const sorted = (v: string) => letters(v).split('').sort().join('');
  const g = letters(given), c = letters(code);
  if (g.length === 0) return true;
  if (g === c || sorted(given) === sorted(code)) return true;
  if (locale && letters(locale).length > 0 && sorted(given) === sorted(locale)) return true;
  const words = given.split(/[^A-Za-z]+/).filter(Boolean);
  return words.length > 0 && words.every((w) => w.length <= 3);
}

export function langLabel(l: { code: string; displayName: string }): string {
  const key = (l.code || '').toLowerCase();
  // v12.9.22 (#885): resolve by the CANONICAL code as well as the raw one, so a regional row ("en-US")
  // finds "English / EN" in the fallback table instead of falling out as its own fragments.
  const canon = canonLang(l.code);
  const info = LANGUAGE_INFO[key] ?? LANGUAGE_INFO[canon];
  const short = info?.short ?? canon.toUpperCase();
  const given = (l.displayName || '').trim();
  // A real name, not a code echoed back - see isCodeShaped above.
  const looksLikeAName = given.length > 2 && !isCodeShaped(given, canon, key);   // v12.9.24 (#904): locale too
  const name = looksLikeAName ? given : (info?.name ?? short);
  return `${name} · ${short}`;
}

// Canonical 2-letter key for matching a language across code styles (en / english / en-US → "en").
export function canonLang(code: string): string {
  const k = (code || '').toLowerCase();
  const info = LANGUAGE_INFO[k];
  return (info ? info.short : k.slice(0, 2)).toLowerCase();
}

// v12.2.20.0 (PRE-TOK2): render the preview with the SAME token rules the runtime uses. Known tenant
// tokens ({organization.practiceName}, {organization.supported_languages}, phone, primary_language)
// resolve to real values fetched from GET /t2/settings/ai-agent; design-time-unknown tokens (caller.*,
// slots, node outputs) show a readable [lastSegment] marker.
export function renderPromptPreview(text: string, ctx: TokenContext): string {
  return resolveTokens(text, ctx);
}

// Shared prompt editor used by EVERY card that configures spoken text (Disclosure blocks, Greeting,
// Language-detect fallback). Wraps the AI-Agent TokenPicker (grouped Insert-token panel + drag/drop +
// `prefix.` autocomplete) in an `.ai-agent-panel` scope. The PREVIEW box is ALWAYS shown (live token
// substitution) and carries a ▶ Play button that speaks the line in the tenant's configured voice via
// the same /t2/settings/ai-agent/voice-preview endpoint the AI Agent uses — because what matters is how
// the caller will hear it.
// v12.9.24 (#911): the texts PUBLISH auto-translated, per language (from the document's `machineFilled` record).
// Provided once by the workflow page, read by every PromptEditor - so all 18+ sites mark machine text without a
// prop each, and a new site cannot forget.
export const MachineFilledContext = createContext<Record<string, Set<string>>>({});

// ══ v12.10.10 (#1408, Tushar OK 10/8): THE BOX CONTRACT. Every box says what happens when it is blank - the engine speaks a
// default (platformDefault: shown as written text with the Template default badge, spoken by Preview / Play) OR nothing is
// said (silentWhenBlank: the note 'Blank = nothing is said.' under the box). Declaring neither does not compile, which is
// how "the build fails otherwise" is enforced without a separate gate. platformDefault may resolve to empty at run time
// (a line not loaded yet) - the box then reads as silent until it arrives.
export type PromptBlankRule =
  | { platformDefault: string | null | undefined; silentWhenBlank?: false }
  | { silentWhenBlank: true; platformDefault?: undefined };

export function PromptEditor(props: PromptEditorBase & PromptBlankRule) {
  const { value, onChange, rows, language, groups, known, translateSource, translateSourceLang, translateSourceDefault, noTokens, blankNote, previewWhenBlank } = props;
  const platformDefault = props.platformDefault ?? undefined;
  return <PromptEditorInner value={value} onChange={onChange} rows={rows} language={language} groups={groups} known={known}
    translateSource={translateSource} translateSourceLang={translateSourceLang} translateSourceDefault={translateSourceDefault}
    noTokens={noTokens} platformDefault={platformDefault ?? undefined} blankNote={blankNote} previewWhenBlank={previewWhenBlank} />;
}

interface PromptEditorBase {
  value: string;
  onChange: (value: string) => void;
  /** v12.10.10 (#1408): IGNORED - no box ever shows a grey placeholder sentence (Tushar 10/8). Kept so old call sites compile. */
  placeholder?: string;
  rows?: number;
  language: string;
  groups?: TokenGroup[];
  known?: string[];
  translateSource?: string;
  translateSourceLang?: string;
  /** v12.10.10 (#1408): the PRIMARY language's default, translated when the primary box itself is blank. */
  translateSourceDefault?: string;
  noTokens?: boolean;
  /** v12.10.10 (#1408): the note under a silent box when "Blank = nothing is said." is not the whole truth. */
  blankNote?: string;
  /** v12.10.16 (#1674): what is spoken when this box is blank and it falls back to ANOTHER box (the Text card's Re-ask =
   *  the offer again). Shown in the PREVIEW only - never written into the box. */
  previewWhenBlank?: string;
}

function PromptEditorInner({ value, onChange, rows, language, groups, known, translateSource, translateSourceLang, translateSourceDefault, noTokens, platformDefault, blankNote, previewWhenBlank }: {
  value: string;
  previewWhenBlank?: string;
  onChange: (value: string) => void;
  translateSourceDefault?: string;
  blankNote?: string;
  rows?: number;
  language: string;
  // v12.2.36.0 (#202): optional workflow-derived token catalog + unresolved-token known-set, so node/step
  // prompts get the SAME editor (Insert-token + PREVIEW + Play) the Disclosure/Greeting/Terminal cards use.
  groups?: TokenGroup[];
  known?: string[];
  // v12.3.7 (#238-F): when the DEFAULT-language sibling text is passed here, an 'Auto-translate' control fills
  // an EMPTY field for THIS language from it (on demand). It never overwrites text the user has typed - the
  // button is disabled once the field has content, and a Reset (clear) is offered instead.
  translateSource?: string;
  translateSourceLang?: string;
  // v12.8.0 (#660): drop the Insert-token button. The ladder's rung sentences take no tokens - his call,
  // 2026-09-23 - and a button that opens a picker for something you may not use is worse than no button.
  noTokens?: boolean;
  // ══ v12.9.4 (#731): THE SENTENCE THAT WILL ACTUALLY BE SPOKEN IF THIS BOX STAYS EMPTY. ═══════════════
  //
  // His rule, 2026-09-25, and he asked for it EVERYWHERE: *"if i remove the sentence, using the platform
  // default comes up. if i try to edit then that sentence still stays there, i modify and using the platform
  // default is gone. **that way we don't mix and it is throughout everywhere.**"*
  //
  // So, when this is passed and the box is empty: the default shows in the box in grey, a badge sits at the TOP
  // RIGHT, and clicking in leaves the sentence there and EDITABLE - you tweak one word rather than retyping it.
  // Text identical to the default is still the default: nothing is authored, and the badge stays. That last
  // part is what stops a click-in/click-out silently authoring the default and freezing it against future
  // platform changes.
  //
  // WHY IT LIVES HERE AND NOT IN EACH CARD: 18 sites render this editor. #691 shipped the same idea for ONE of
  // them and put it in a note UNDER the preview - one element away from the control being edited - which is how
  // #725 stayed hidden: he cleared a rung box to test new wording and could not see which sentence it would
  // fall back to.
  platformDefault?: string;
}) {
  const authored = (value ?? '').trim();
  const usingDefault = !authored && !!(platformDefault ?? '').trim();
  // v12.10.10 (#1408): the default is ALWAYS shown as written text - focused or not (#731 showed it grey until clicked).
  // Editing changes it; clearing everything returns to the default (handleChange stores '' for "same as default").
  // v12.10.27 (#2168): a box the person CLEARED stays empty while they work in it - the template default no longer jumps back in
  // (select all + delete refilled "Thank you for calling." and the next key landed in front of it). Reset brings the default back.
  // Stored value is still '' = "same as the template default", so what the engine says is unchanged.
  const [cleared, setCleared] = useState(false);
  useEffect(() => { setCleared(false); }, [language]);
  const shownValue = usingDefault && !cleared ? platformDefault! : value;
  const silent = !(platformDefault ?? '').trim();
  // What the preview and Play speak: identical to what the engine would speak.
  // v12.10.16 (#1674): a box whose blank means "another line again" previews that line, not "Nothing is said."
  const effectiveText = authored ? value : ((platformDefault ?? '').trim() || (previewWhenBlank ?? '').trim() || value);
  const handleChange = (next: string) => {
    const d = (platformDefault ?? '').trim();
    // v12.9.21 (#845/#310): a HUMAN has touched this text, so the machine-drafted mark clears here and
    // only here. translate() writes through onChange directly and never passes through this path, which
    // is what keeps the two cases distinguishable.
    setMachineDrafted(false);
    setCleared(!next.trim() && !!d);   // v12.10.27 (#2168)
    // Unchanged from the default is still the default - nothing authored, badge stays.
    onChange(d && next.trim() === d ? '' : next);
  };
  const [playState, setPlayState] = useState<'idle' | 'playing' | 'error'>('idle');
  const [playMsg, setPlayMsg] = useState<string>('');
  const { data: ai } = useQuery<AiAgentTokenSettings>({
    queryKey: ['t2-ai-agent'],
    queryFn: () => api.get<AiAgentTokenSettings>('/t2/settings/ai-agent'),
    staleTime: 60_000,
  });
  // v12.2.43.0 (#236): tenant Vocabulary map, so the Insert-token chips + autocomplete DISPLAY the tenant label.
  const { data: orgVocab } = useQuery<OrgVocabResponse>({
    queryKey: ['t2-org-vocabulary'],
    queryFn: () => api.get<OrgVocabResponse>('/t2/settings/organization'),
    staleTime: 60_000,
  });
  // v12.3.10 (#296): a caller-supplied catalog (the workflow designer passes captured slots) still wins;
  // otherwise the panel is built from the served vocabulary catalog rather than a hardcoded list.
  // v12.4.16 (#499): the tenant's own fields are offered beside the platform ones.
  const effectiveGroups = groups ?? catalogGroups(orgVocab?.vocabularyCatalog, orgVocab?.vocabulary ?? {}, orgVocab?.customFields ?? {}, orgVocab?.lists ?? {});   // v12.7.3 (#649)
  const vocabLabelOf = (t: string) => tokenLabel(t, orgVocab?.vocabulary ?? {});
  const vocabAliasOf = makeAliasOf(orgVocab?.vocabulary ?? {});   // v12.2.43.1 (#236/#246): typed-prefix -> canonical entity
  // v12.10.13 (#1600): the preview speaks the language of the tab being edited - {call.timeGreeting} on the ES tab reads
  // "Buenas noches", as the engine says it on a Spanish call (it showed "Good evening").
  const previewCtx = { ...tokenContextFrom(ai, orgVocab?.identity), lang: canonLang(language) || language || 'en' };
  const preview = renderPromptPreview(value, previewCtx);
  // v12.2.38.0 (#206): segmented preview so capture-token samples render distinctly, with a footnote.
  // v12.9.4 (#731): the preview speaks what the ENGINE would speak - the authored text, or the platform
  // default when the box is empty. Previewing an empty box as "Nothing to say yet" was true of the box and
  // false of the call.
  const previewSegments = resolveTokenSegments(effectiveText, previewCtx);
  const hasSample = previewSegments.some(seg => seg.kind === 'sample');
  // v12.2.40.0 (#226): Play must SPEAK what the preview SHOWS - tenant values + sample values substituted - not
  // the raw {tokens}. Join the segment text (samples included) instead of the bracket-marker string.
  const spokenPreview = previewSegments.map(seg => seg.text).join('');

  async function play() {
    const sample = spokenPreview.trim();
    if (!sample) return;
    const langs = ai?.languages ?? [];
    const match = langs.find(l => canonLang(l.code) === canonLang(language)) ?? langs[0];
    const voiceId = match?.voice;
    setPlayState('playing'); setPlayMsg('Synthesizing…');
    try {
      const res = await api.post<{ ok?: boolean; reason?: string | null; reference?: string | null; audioUrl?: string | null }>(
        '/t2/settings/ai-agent/voice-preview', { language: match?.code ?? language, voiceId, sample });
      if (res.audioUrl) {
        setPlayMsg('▶ Playing');
        try { await new Audio(res.audioUrl).play(); setPlayState('idle'); setPlayMsg(''); }
        catch { setPlayState('error'); setPlayMsg('Tap the page, then Play again (autoplay was blocked).'); }
      } else {
        setPlayState('error'); setPlayMsg(previewFailureText(res));
      }
    } catch {
      setPlayState('error'); setPlayMsg('Preview failed — the voice service did not respond.');
    }
  }

  // v12.3.7 (#238-F): on-demand auto-translate of an EMPTY field from the default-language text.
  const [xlState, setXlState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [xlMsg, setXlMsg] = useState<string>('');
  // v12.9.21 (#845, answering #310): unreviewed machine Spanish reached callers once already - three rows
  // said "ano" where they meant "ano-with-tilde" and nobody knew the text had never been read by a human.
  // A box filled by the machine now SAYS SO until a person edits it.
  // KNOWN LIMIT, stated so it is not mistaken for the whole of #310: this mark lives in component state,
  // so it marks the moment of translation, not the history of the field. It does NOT survive a reload.
  // The durable signal needs a per-field flag stored WITH the text, which is a data-model change across
  // every prompt-bearing structure - see #310. Do not close #310 on this.
  const [machineDrafted, setMachineDrafted] = useState(false);
  // v12.9.24 (#911): publish filled this exact text for this language. Editing it makes it the tenant's
  // (the server drops the mark on the next publish because the text no longer matches).
  const machineTexts = useContext(MachineFilledContext);
  const publishFilled = !!(value ?? '').trim() && !!machineTexts[language]?.has((value ?? '').trim());
  // v12.10.10 (#1408): ALWAYS shown on a non-primary tab; greyed with 'Write the English text first' when there is nothing to translate.
  const showTranslate = !!(translateSourceLang && canonLang(translateSourceLang) !== canonLang(language));
  const sourceText = (translateSource ?? '').trim() || (translateSourceDefault ?? '').trim();
  const canTranslate = showTranslate && !!sourceText;
  async function translate() {
    const src = sourceText;
    if (!src || value.trim()) return;   // only fill an empty field; never overwrite edited text
    setXlState('loading'); setXlMsg('');
    try {
      const res = await api.post<{ ok?: boolean; translated?: string | null; reason?: string | null }>(
        '/t2/workflow/translate', { text: src, sourceLang: translateSourceLang, targetLang: language });
      if (res.ok && res.translated) { onChange(res.translated); setMachineDrafted(true); setXlState('idle'); setXlMsg(''); }
      else { setXlState('error'); setXlMsg(res.reason === 'no-key' ? 'No AI key configured - type it manually.' : 'Could not translate - type it manually.'); }
    } catch { setXlState('error'); setXlMsg('Translate failed - type it manually.'); }
  }

  // v12.3.10 (#300): Auto-translate + Reset now sit at the right of the Insert-token row instead of on
  // their own line underneath, where they read as unrelated to the field they act on.
  const translateControls = (
    <>
      <button type="button" className="tenant-xl-btn" disabled={!canTranslate || xlState === 'loading' || !!value.trim()}
        title={!canTranslate ? `Write the ${languageWord(translateSourceLang)} text first` : value.trim() ? 'Clear this field (Reset) first to auto-translate' : `Translate the ${(translateSourceLang || '').toUpperCase()} text into ${language.toUpperCase()}`}
        onClick={translate}>
        {xlState === 'loading' ? '\u2026 Translating' : `\u2728 Auto-translate from ${(translateSourceLang || '').toUpperCase()}`}
      </button>
      {/* v12.10.10 (#1408): Reset is always there; with nothing typed it has nothing to clear. */}
      {(
        <button type="button" className="tenant-xl-reset" disabled={!value.trim() && !cleared} title={value.trim() || cleared ? "Back to the template default" : 'Nothing to reset'}
          onClick={() => { onChange(''); setCleared(false); setMachineDrafted(false); setXlState('idle'); setXlMsg(''); }}>{'\u21bb'} Reset</button>
      )}
      {!canTranslate && <small className="tenant-xl-msg">Write the {languageWord(translateSourceLang)} text first</small>}
      {xlMsg && <small className={`tenant-xl-msg${xlState === 'error' ? ' is-error' : ''}`}>{xlMsg}</small>}
    </>
  );

  return (
    <div className="tenant-prompt-editor">
      <div className={'ai-agent-panel tenant-tokenwrap' + (usingDefault ? ' rq-usingdefault' : '')}>
        {/* v12.9.4 (#731): the badge, top right of the box, so the state is visible without reading the
            preview line underneath. His choice of placement, 2026-09-25. */}
        {/* v12.9.5 (#738): it is the TEMPLATE's sentence now, not a platform literal, and the badge says so.
            His words: "the word can be changed to 'Template Default' or just 'Template'". */}
        {usingDefault && !cleared && <span className="rq-defbadge" aria-hidden="true">Template default</span>}
        {/* v12.9.21 (#845/#310): the quality signal. It sits where the Template-default badge sits so the
            box's state is readable without hunting, and it is deliberately worded as an instruction rather
            than a label - "machine-translated" alone tells a reviewer what happened, not what to do. */}
        {/* v12.9.26 (#946, his decision): the per-field Machine-translated badge is GONE - unnecessary alarm. The
            hidden machineFilled record stays for tracking and is never shown. */}
        {false && (machineDrafted || publishFilled) && null}
        <TokenPicker value={shownValue} onChange={handleChange}
          rows={rows} groups={effectiveGroups} labelOf={vocabLabelOf} aliasOf={vocabAliasOf}
          noTokens={noTokens} rightSlot={showTranslate ? translateControls : undefined} lang={canonLang(language) || undefined} />
      </div>
      {/* v12.10.10 (#1408): a box that is silent when blank says so UNDER the box - never as grey text inside it. */}
      {cleared && usingDefault && <small className="tenant-field-note rq-blanknote">Empty - the template default is said. Reset shows it.</small>}
      {silent && !authored && <small className="tenant-field-note rq-blanknote">{blankNote || 'Blank = nothing is said.'}</small>}
      {/* v12.10.11.1 (#1481): every box flags an unknown {token} - a box with no workflow list checks against its own token catalog. */}
      {(known || effectiveGroups) && <UnresolvedTokenNote text={effectiveText} known={known ?? (effectiveGroups ?? []).flatMap((g) => g.tokens)} />}
      <div className="tenant-prompt-preview">
        <div className="tenant-prompt-preview-head">
          <span>PREVIEW · {language.toUpperCase()}</span>
          <button type="button" className="tenant-play-btn" onClick={play} disabled={playState === 'playing' || !spokenPreview.trim()}>
            {playState === 'playing' ? '● Playing…' : '▶ Play'}
          </button>
        </div>
        <p>
          {previewSegments.length === 0
            ? 'Nothing is said.'
            : previewSegments.map((seg, i) =>
                seg.kind === 'sample'
                  ? <span key={i} className="tenant-preview-sample" title="Sample value — replaced with the caller’s captured value on a live call">{seg.text}</span>
                  : seg.kind === 'marker'
                    ? <span key={i} className="tenant-preview-marker">{seg.text}</span>
                    : <span key={i}>{seg.text}</span>)}
        </p>
        {hasSample && <small className="tenant-preview-footnote">* Highlighted values are samples — replaced with the caller’s captured values on a live call.</small>}
        {playMsg && <small className={`tenant-play-msg${playState === 'error' ? ' is-error' : ''}`}>{playMsg}</small>}
      </div>
    </div>
  );
}

/** v12.10.10 (#1408): the source language's name for 'Write the English text first'. */
function languageWord(code?: string): string {
  const c = canonLang(code ?? '');
  return LANGUAGE_INFO[c]?.name ?? (c ? c.toUpperCase() : 'primary language');
}

// VoiceSpectrum.Web.T2 · v12.9.21 · PromptEditor (shared) · #845 machine-drafted mark
